import { useEffect, useState, useCallback } from 'react';
import { Loader2, AlertCircle, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';
import { projectConfigAPI } from '../services/api';
import { InlineLoader } from '../components/loading';
import { useAuthStore } from '../stores/authStore';

// Master Management → Grooming Prompt.
// Lets a Super Admin override the Gemini grooming-analysis prompt per project
// (multi-tenant: each client program has different uniform/background needs).
// The full prompt is editable; the backend rejects a save that drops the
// required OUTPUT-JSON keys so analysis can't silently break.
export default function GroomingPromptPage() {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);

  const [promptText, setPromptText] = useState('');
  const [defaultText, setDefaultText] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const fetchPrompt = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const res = await projectConfigAPI.getGroomingPrompt(selectedProjectId);
      const data = res.data?.data;
      setPromptText(data?.promptText || '');
      setDefaultText(data?.defaultText || '');
      setIsCustom(!!data?.isCustom);
      setDirty(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load grooming prompt');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchPrompt();
  }, [fetchPrompt]);

  const handleSave = async () => {
    if (!selectedProjectId) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await projectConfigAPI.saveGroomingPrompt(selectedProjectId, promptText);
      setSuccess('Grooming prompt saved for this project.');
      setIsCustom(true);
      setDirty(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save grooming prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedProjectId) return;
    if (!window.confirm('Reset this project to the built-in default grooming prompt?')) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await projectConfigAPI.resetGroomingPrompt(selectedProjectId);
      setPromptText(defaultText);
      setIsCustom(false);
      setDirty(false);
      setSuccess('Reverted to the default grooming prompt.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset grooming prompt');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedProjectId) {
    return (
      <div className="rounded-lg border border-gray-100 bg-white p-6 text-sm text-gray-600">
        Select a project (top-right “Switch Project”) to edit its grooming prompt.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Grooming Check Instructions</h1>
          <p className="mt-1 text-sm text-gray-500">
            The instruction sent to Gemini when scoring attendance selfies for this project.{' '}
            <span
              className={`font-medium ${isCustom ? 'text-primary-700' : 'text-gray-500'}`}
            >
              {isCustom ? 'Using a custom prompt' : 'Using the built-in default'}
            </span>
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            disabled={saving || loading || !isCustom}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to default
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || !dirty}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <p>
          Keep the <strong>OUTPUT JSON</strong> section at the end of the prompt intact — the app
          reads <code>uniformScore</code>, <code>backgroundScore</code>, <code>groomingScore</code>,
          and <code>subFactors</code> from it. Saving a prompt that drops those keys is blocked, and
          changing the scoring rules will change every employee’s grooming result for this project.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2 rounded-lg border border-green-100 bg-green-50 p-3 text-sm text-green-700">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        {loading ? (
          <InlineLoader label="Loading prompt…" className="flex justify-center p-12" />
        ) : (
          <textarea
            value={promptText}
            onChange={(e) => {
              setPromptText(e.target.value);
              setDirty(true);
              setSuccess(null);
            }}
            spellCheck={false}
            rows={28}
            className="block w-full resize-y border-0 p-4 font-mono text-xs leading-relaxed text-gray-800 focus:outline-none focus:ring-0"
            placeholder="Grooming analysis prompt…"
          />
        )}
      </div>
    </div>
  );
}
